import { useCallback, useEffect, useRef, forwardRef } from 'react';
import { Input, Button, Icon, Spinner } from '../';

import type { SearchFormProps } from './SearchForm.types';
import styles from './SearchForm.module.css';

/**
 * SearchForm component for searching assets and folders
 * Provides controlled input with debouncing, clear button, and optional search button
 *
 * @example
 * ```tsx
 * // Basic usage with controlled input
 * <SearchForm
 *   value={searchQuery}
 *   onChange={setSearchQuery}
 *   onSubmit={handleSearch}
 *   placeholder="Search assets..."
 * />
 *
 * // With search button and debouncing
 * <SearchForm
 *   value={searchQuery}
 *   onChange={setSearchQuery}
 *   onSubmit={handleSearch}
 *   showButton
 *   buttonText="Search"
 *   debounceMs={500}
 * />
 *
 * // Loading state
 * <SearchForm
 *   value={searchQuery}
 *   onChange={setSearchQuery}
 *   onSubmit={handleSearch}
 *   loading={isSearching}
 * />
 * ```
 */
export const SearchForm = forwardRef<HTMLInputElement, SearchFormProps>(
  (
    {
      value,
      onChange,
      onSubmit,
      onClear,
      placeholder = 'Search...',
      size = 'md',
      autoFocus = false,
      showButton = false,
      buttonText = 'Search',
      loading = false,
      disabled = false,
      debounceMs = 0,
      className,
      'data-testid': testId,
      dataTestId,
      dataId,
    },
    ref
  ) => {
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const internalRef = useRef<HTMLInputElement>(null);
    const inputRef = (ref as React.RefObject<HTMLInputElement>) || internalRef;

    // Cleanup debounce timer on unmount
    useEffect(() => {
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, []);

    // Handle input change with optional debouncing
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        onChange(newValue);

        if (debounceMs > 0) {
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }
          debounceTimerRef.current = setTimeout(() => {
            onSubmit(newValue);
          }, debounceMs);
        }
      },
      [onChange, onSubmit, debounceMs]
    );

    // Handle form submission
    const handleSubmit = useCallback(
      (e?: React.FormEvent) => {
        e?.preventDefault();

        // Clear any pending debounce
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }

        onSubmit(value);
      },
      [onSubmit, value]
    );

    // Handle Enter key press
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleSubmit();
        }
      },
      [handleSubmit]
    );

    // Handle clear button click
    const handleClear = useCallback(() => {
      // Clear any pending debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      onChange('');
      onClear?.();

      // Focus input after clear
      inputRef.current?.focus();
    }, [onChange, onClear, inputRef]);

    const hasValue = value.length > 0;
    const isDisabled = disabled || loading;

    // Build left element - search icon or spinner
    const leftElement = loading ? (
      <Spinner size="sm" label="Searching..." data-testid={testId && `${testId}-spinner`} />
    ) : (
      <Icon name="search" size="sm" aria-hidden />
    );

    // Build right element - clear button
    const rightElement = hasValue && !isDisabled ? (
      <button
        type="button"
        className={styles.clearButton}
        onClick={handleClear}
        aria-label="Clear search"
        data-testid={testId && `${testId}-clear`}
      >
        <Icon name="close" size="sm" />
      </button>
    ) : null;

    const wrapperClassNames = [styles.searchForm, className].filter(Boolean).join(' ');

    return (
      <form
        className={wrapperClassNames}
        onSubmit={handleSubmit}
        role="search"
        data-size={size}
        data-testid={dataTestId || testId}
        data-id={dataId}
      >
        <div className={styles.inputWrapper}>
          <Input
            ref={inputRef}
            type="search"
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            size={size}
            disabled={isDisabled}
            autoFocus={autoFocus}
            leftElement={leftElement}
            rightElement={rightElement}
            fullWidth
            aria-label={placeholder}
            data-testid={testId && `${testId}-input`}
          />
        </div>

        {showButton && (
          <Button
            type="submit"
            variant="primary"
            size={size}
            disabled={isDisabled}
            loading={loading}
            aria-label={buttonText}
            data-testid={testId && `${testId}-button`}
          >
            {buttonText}
          </Button>
        )}
      </form>
    );
  }
);

SearchForm.displayName = 'SearchForm';

export default SearchForm;